import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of, throwError } from 'rxjs';
import { catchError, filter, map, retry, switchMap, tap } from 'rxjs/operators';
import { Firestore, doc, docData, docSnapshots, setDoc, updateDoc } from '@angular/fire/firestore';
import { Merchant, User } from '@print2go/types';
import { AuthService } from '../../shared/services/auth.service';
import {
  loadUserSuccess,
  loadUserFailure,
  logoutUser,
  updateUser,
  updateUserSuccess,
  setAuthState,
} from '../actions/user.actions';
import { filterTruthy } from '../../shared/utils/operator/filter-truthy';
import { UserInfo } from '@angular/fire/auth';

@Injectable()
export class UserEffects {
  private firestore = inject(Firestore);
  private actions$ = inject(Actions);
  private authService = inject(AuthService);

  authState$ = createEffect(() => {
    return this.authService.ref.state$
      .pipe(
        map(user => {
          if (!user) return setAuthState({ user: null });

          const userInfo: UserInfo & Pick<User, 'emailVerified'> = {
            displayName: user.displayName,
            email: user.email,
            emailVerified: user.emailVerified,
            phoneNumber: user.phoneNumber,
            photoURL: user.photoURL,
            providerId: user.providerId,
            uid: user.uid,
          }
          return setAuthState({ user: userInfo });
        }),
      );
  });

  loadUser$ = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(setAuthState),
        switchMap(({ user: firebaseUser }) => {
          if (!firebaseUser) return of(logoutUser());

          const userRef = doc(this.firestore, 'merchants', firebaseUser.uid);
          return docSnapshots(userRef)
            .pipe(
              tap(async snapshot => {
                if (snapshot.metadata.fromCache) return;
                if (snapshot.exists()) return;
                const now = new Date();
                const data = {
                  metadata: {
                    createdAt: now,
                    updatedAt: now,
                  },
                  ...firebaseUser,
                };
                await setDoc(userRef, data);
              }),
              map(snapshot => {
                return snapshot.data() as Merchant;
              }),
              filterTruthy(),
              map(user => {
                return loadUserSuccess({ user });
              }),
              catchError(error => {
                return of(loadUserFailure(error));
              }),
              retry(),
            )
        })
      )
  });
  
  updateUser$ = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(updateUser),
        switchMap(({ data }) => {
          const { uid, ...userUpdates } = data;
          const userRef = doc(this.firestore, 'merchants', uid);
          return from(setDoc(userRef, userUpdates, { merge: true }))
        }),
        map(() => {
          return updateUserSuccess();
        }),
        catchError(error => {
          return of(loadUserFailure({ error }));
        })
      );
  });
}